import type { MountedExperiment, SliderRowHandle } from '@learnforge/demo-kit';
import { createSliderRow } from '@learnforge/demo-kit';
import { parse, sampleCurve, transformExpression, type ExprNode } from '../func';
import { createActionBar } from '../action-bar';
import { createFunctionGraph, type FunctionGraphHandle } from '../render/function-graph';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianViewportHandle,
  type CartesianViewportView,
} from '@learnforge/demo-render';
import { stringify } from '@learnforge/demo-math/expr';
import { TRANSFORMATION_PRESETS } from '../data/transformation-presets';
import type { FuncContext } from '../types';
import {
  createTransformationAnnotations,
  type TransformationAnnotationsHandle,
} from '../render/transformation-annotations';

const BASES: { id: string; label: string }[] = [
  { id: 'x', label: 'x' },
  { id: 'x^2', label: 'x²' },
  { id: 'x^3', label: 'x³' },
  { id: 'sqrt(x)', label: '√x' },
  { id: 'abs(x)', label: '|x|' },
  { id: '1/x', label: '1/x' },
  { id: 'exp(x)', label: 'e^x' },
  { id: 'log(x)', label: 'ln(x)' },
  { id: 'sin(x)', label: 'sin(x)' },
  { id: 'cos(x)', label: 'cos(x)' },
  { id: 'tan(x)', label: 'tan(x)' },
];

function uniqueValues(values: readonly number[]): number[] {
  return values.filter(
    (value, index) => values.findIndex((candidate) => Math.abs(candidate - value) < 1e-6) === index,
  );
}

function verticalAsymptotesForBase(
  base: string,
  b: number,
  h: number,
  xDomain: readonly [number, number],
): number[] {
  if (base === '1/x' || base === 'log(x)') return [h];
  if (base !== 'tan(x)' || b === 0) return [];

  const [xMin, xMax] = xDomain;
  const first = Math.ceil((b * (xMin - h) - Math.PI / 2) / Math.PI);
  const last = Math.floor((b * (xMax - h) - Math.PI / 2) / Math.PI);
  return Array.from({ length: Math.max(0, last - first + 1) }, (_, index) =>
    Number((h + (Math.PI / 2 + (first + index) * Math.PI) / b).toFixed(12)),
  );
}

export function stepwiseFrames(
  base: ExprNode,
  target: { a: number; b: number; h: number; k: number },
): ExprNode[] {
  return [
    base,
    transformExpression(base, { a: target.a, b: 1, h: 0, k: 0 }),
    transformExpression(base, { a: target.a, b: target.b, h: 0, k: 0 }),
    transformExpression(base, { a: target.a, b: target.b, h: target.h, k: 0 }),
    transformExpression(base, { a: target.a, b: target.b, h: target.h, k: target.k }),
  ];
}

export function mountTransformations(ctx: FuncContext): MountedExperiment {
  let baseSrc = 'sin(x)';
  let params: { a: number; b: number; h: number; k: number } = { a: 1, b: 1, h: 0, k: 0 };

  const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'fn-stage' });
  ctx.root.appendChild(svg);
  const stageG = createSvgEl('g');
  svg.appendChild(stageG);
  const legend = createFormulaLegend(ctx.root);
  let view: CartesianViewportView = { xDomain: [-10, 10], yDomain: [-10, 10], zoom: 1 };
  let viewport: CartesianViewportHandle | null = null;

  let graphHandle: FunctionGraphHandle | null = null;
  let annotationsHandle: TransformationAnnotationsHandle | null = null;
  function redraw(): void {
    const baseNode = parse(baseSrc).node;
    const transformed = transformExpression(baseNode, params);
    const sampleCount = Math.max(320, Math.round(800 * (globalThis.devicePixelRatio || 1)));
    const parentSample = sampleCurve(baseNode, view.xDomain[0], view.xDomain[1], sampleCount);
    const xfSample = sampleCurve(transformed, view.xDomain[0], view.xDomain[1], sampleCount);
    const parentDiscontinuities = verticalAsymptotesForBase(baseSrc, 1, 0, view.xDomain);
    const transformedDiscontinuities = verticalAsymptotesForBase(
      baseSrc,
      params.b,
      params.h,
      view.xDomain,
    );
    const verticalAsymptotes = uniqueValues([
      ...parentDiscontinuities,
      ...transformedDiscontinuities,
    ]);
    const options = {
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 800,
      height: 460,
      curves: [
        {
          sample: parentSample,
          className: 'fn-curve-parent',
          label: 'p(x)',
          discontinuities: parentDiscontinuities,
        },
        {
          sample: xfSample,
          className: 'fn-curve-primary',
          label: 'f(x)',
          discontinuities: transformedDiscontinuities,
        },
      ],
      asymptotes: verticalAsymptotes.map((value) => ({ kind: 'vertical' as const, value })),
      i18n: ctx.i18n,
    };
    if (graphHandle) graphHandle.update(options);
    else graphHandle = createFunctionGraph(stageG, options);
    if (!annotationsHandle) annotationsHandle = createTransformationAnnotations(stageG);
    annotationsHandle.update({
      base: baseSrc,
      ...params,
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 800,
      height: 460,
    });
    legend.setEntries([
      { id: 'transformed', formula: `f(x) = ${stringify(transformed)}`, color: 'var(--accent)' },
      { id: 'parent', formula: `p(x) = ${baseSrc}`, color: 'var(--ink)', lineStyle: 'solid' },
    ]);
  }

  const baseRow = document.createElement('div');
  baseRow.className = 'fn-panel-row fn-dropdown-row';
  const baseLabel = document.createElement('label');
  baseLabel.textContent = ctx.i18n.t('param.base.label');
  const baseSelect = document.createElement('select');
  for (const b of BASES) {
    const opt = document.createElement('option');
    opt.value = b.id;
    opt.textContent = b.label;
    baseSelect.appendChild(opt);
  }
  baseSelect.value = baseSrc;
  baseSelect.addEventListener('change', () => {
    baseSrc = baseSelect.value;
    viewport?.setBase({ xDomain: [-10, 10], yDomain: [-10, 10] });
  });
  baseRow.appendChild(baseLabel);
  baseRow.appendChild(baseSelect);
  ctx.panel.appendChild(baseRow);

  const sliderHandles: Record<keyof typeof params, SliderRowHandle> = {} as Record<
    keyof typeof params,
    SliderRowHandle
  >;
  function addSlider(labelKey: string, key: keyof typeof params, min: number, max: number): void {
    const handle = createSliderRow({
      id: `xf-${key}`,
      labelKey,
      i18n: ctx.i18n,
      min,
      max,
      step: 0.1,
      value: params[key],
      format: (v) => v.toFixed(2),
      onChange: (v) => {
        params[key] = v;
        redraw();
      },
    });
    sliderHandles[key] = handle;
    ctx.panel.appendChild(handle.element);
  }
  addSlider('param.a.label', 'a', -3, 3);
  addSlider('param.b.label', 'b', 0.1, 3);
  addSlider('param.h.label', 'h', -5, 5);
  addSlider('param.k.label', 'k', -5, 5);

  function syncSliders(): void {
    sliderHandles.a.setValue(params.a);
    sliderHandles.b.setValue(params.b);
    sliderHandles.h.setValue(params.h);
    sliderHandles.k.setValue(params.k);
  }

  redraw();
  viewport = createCartesianViewport(svg, {
    base: { xDomain: [-10, 10], yDomain: [-10, 10] },
    plotRect: { x: 0, y: 0, width: 800, height: 460 },
    onChange(next) {
      view = next;
      redraw();
    },
  });

  createActionBar(
    ctx.actionBar,
    [
      {
        id: 'reset',
        labelKey: ctx.i18n.t('btn.reset'),
        onClick: () => {
          params = { a: 1, b: 1, h: 0, k: 0 };
          syncSliders();
          redraw();
        },
      },
      {
        id: 'animate',
        labelKey: ctx.i18n.t('btn.animate'),
        onClick: () => {
          const preset = TRANSFORMATION_PRESETS[0];
          if (!preset) return;
          baseSrc = preset.base;
          baseSelect.value = baseSrc;
          viewport?.setBase({ xDomain: [-10, 10], yDomain: [-10, 10] });
          void (async () => {
            for (const frame of preset.frames) {
              params = { a: frame.a, b: frame.b, h: frame.h, k: frame.k };
              syncSliders();
              redraw();
              await new Promise((res) => setTimeout(res, frame.pauseMs));
            }
          })();
        },
      },
    ],
    { viewReset: { labelKey: ctx.i18n.t('btn.reset-view'), onResetView: () => viewport?.reset() } },
  );
  return {
    dispose() {
      graphHandle?.destroy();
      annotationsHandle?.destroy();
      viewport?.dispose();
      legend.destroy();
      svg.remove();
      ctx.panel.replaceChildren();
      ctx.actionBar.replaceChildren();
    },
  };
}
